/**
 * Formulas menu: list of saved formulas to choose from
 */

import {InputComponent} from 'common/components/input';
import {ItemClickEvent, Section} from 'common/components/items-menu/types';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {Metric} from 'app_api/projects/metrics/types';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {FeatureGate, Permission} from 'common/report/constants';

import {ChildMenu} from '../child-menu';
import {canUseImprovedQbMenus} from '../utils/util';
import css from './index.inline.styl';
import {template} from './template';
import {generateFormulaContextPanelData, renderMetricContextPanel} from './context-panel';

interface FormulasMenuState {
  searchFilter: string;
}

export type FormulaMenuItemValue = {
  id?: Metric['id'];
  definition?: string;
};

const RECENT_ITEMS_SUB_KEY = `formula`;

export class FormulasMenu extends ChildMenu<FormulasMenuState> {
  static contextPanelRenderer = renderMetricContextPanel;
  public searchInputEl: InputComponent = null;

  get config() {
    return {
      contexts: [`sessionStorageManager` as const, `currentProject` as const, `metrics` as const],
      defaultContexts: {
        sessionStorageManager: new DummyStorageManager(),
        metrics: new MetricsResource(),
      },
      defaultState: {
        searchFilter: ``,
      },
      helpers: {
        ...super.config.helpers,
      },
      useShadowDom: true,
      css,
      template,
    };
  }

  canShare() {
    return this.getContext(`currentProject`).get()?.value?.getFeatureGate(FeatureGate.Sharing);
  }

  canWriteMetrics() {
    return this.getContext(`currentProject`).get()?.value?.hasPermission(Permission.WriteMetrics);
  }

  handleClickCreate() {
    this.dispatchEvent(new CustomEvent(`create`));
  }

  handleClickItem({detail}: ItemClickEvent<FormulaMenuItemValue>) {
    const {item} = detail;

    if (item.value.id) {
      this.addRecentItem(RECENT_ITEMS_SUB_KEY, item.value);
    }

    this.dispatchEvent(new CustomEvent(`select`, {detail: {item: item.value}}));
  }

  getPersistenceKey() {
    return `formulas-menu`;
  }

  getItemUniqueKey(item: FormulaMenuItemValue) {
    return item.id;
  }

  getSections(): Array<Section<FormulaMenuItemValue>> {
    /** filters metrics according to the user's permissions and sharing */
    const savedFormulas = this.getContext(`metrics`)
      .getAllValues()
      .filter((metric) => metric.type === `formula` && metric.can_view);

    if (savedFormulas.length) {
      const recentFormulaMenuItems = this.getRecentItems(RECENT_ITEMS_SUB_KEY, 3) as FormulaMenuItemValue[];
      const recentFormulas = savedFormulas.filter((formula) =>
        recentFormulaMenuItems.some((item) => item.id === formula.id),
      );

      const showRecents = savedFormulas.length > 6 && recentFormulas.length > 0;

      const getMenuItem = (metric: Metric) => ({
        label: metric.name,
        value: {
          id: metric.id,
        },
      });

      return [
        /** only disply a recently used section when there are enough items to fill in the dropdown */
        {
          label: `Recents`,
          items: showRecents ? recentFormulas.map(getMenuItem) : [],
        },
        {
          label: showRecents ? `All Formulas` : ``,
          items: savedFormulas.map(getMenuItem),
        },
      ].filter(Boolean);
    } else {
      return [];
    }
  }

  /** appends item for an inline formula using the searchFilter as the definition */
  sectionsFromInput = (searchFilter: string): Array<Section<FormulaMenuItemValue>> => {
    const label = `"${searchFilter}"`;
    return [
      {
        items: [
          {
            label,
            value: {
              definition: searchFilter,
            },
            itemFromInput: true,
          },
        ],
      },
    ];
  };

  selectedLabel() {
    const selected = this.attr(`selected`);

    if (selected?.id) {
      return (
        getResolvedFormulaEntry({
          metricsResource: this.getContext(`metrics`),
          formulaEntry: {event: selected},
        })?.event.name || `Loading...`
      );
    }

    return `Select Formula`;
  }

  getSelectedHoverTooltipData() {
    return this.getTooltipData(this.attr(`selected`)?.id);
  }

  getTooltipData(id: number): ContextPanelData {
    return generateFormulaContextPanelData({
      id,
      metricsContext: this.getContext(`metrics`),
      canWriteMetrics: this.canWriteMetrics(),
      canShare: this.canShare(),
    });
  }

  canUseImprovedQbMenus(): boolean {
    return canUseImprovedQbMenus(this.getContext(`currentProject`).get().value);
  }
}

export const FormulasMenuEl = defineAndMakeJsxFactory(`mp-formulas-menu`, FormulasMenu);
export const FormulasMenuReact = panel2react(`mp-formulas-menu`, FormulasMenu);
